import React, { useRef, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import '../../../Styles/StudySpace.css';
import LoadingOverlay from '../../Partials/LoadingOverlay';
import { Add01Icon, Book01Icon, Edit02Icon, Upload03Icon } from 'hugeicons-react';
import { NotFound } from '../../Auth/404';
import { useToast } from '../../../contexts/ToastContext';
import { useWindow } from '../../../contexts/WindowContext';
import { useUnifiedData } from '../../../contexts/AppDataContext';
import { useSpaceContext } from '../../../contexts/AppDataContext';
import { Resource } from '../../../types/resource';
import { Header } from '../../Header/Header';
import { SpaceUploadModal } from './SpaceUploadModal';
import { AttachmentsList } from './AttachmentsList';
import { ChatsSection } from './ChatsSection';
import { chatConfig } from '../../../config/chatConfig';

const StudySpace: React.FC = () => {
  const { openConfirmationModal } = useWindow();
  const navigate = useNavigate();
  const instructionsTextareaRef = useRef<HTMLTextAreaElement>(null);
  const toast = useToast();

  const [isAttachmentToggled, setIsAttachmentToggled] = useState<boolean>(() => {
    try {
      return localStorage.getItem('studySpace.isAttachmentToggled') === 'true';
    } catch {
      return false;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem('studySpace.isAttachmentToggled', String(isAttachmentToggled));
    } catch {
      // ignore
    }
  }, [isAttachmentToggled]);
  
  const {
    currentSpace,
    handleUpdateSpaceTitle,
    handleUpdateSpaceInstructions,
    isEditingTitle,
    setIsEditingTitle,
    currentSpaceId,
    instructions,
    setInstructions,
    title,
    setTitle,
    currentSpaceChats,
    isSpaceChatsLoading,
    openUploadModal,
    handleCreateSpaceChat,
    spaceAttachments,
    removeTextAttachmentFromSpace,
  } = useSpaceContext();
  
  const {   
    isChatsLoading, 
    isSpacesLoading,
    deleteSpace: deleteSpaceFromDataContext,
  } = useUnifiedData();
  
  const handleResourceClick = (resource: Resource) => {
    navigate(`/${resource.chatId}`, {
      state: { title: resource.title }
    });
  };

  const handleAttachmentRemove = (attachment: Resource) => {
    const url = attachment.url;
    const spaceId = currentSpaceId;
    if (!url || !spaceId) return;
    openConfirmationModal(
      "Are you sure you want to remove this attachment?",
      async () => {
        try {
          await removeTextAttachmentFromSpace(spaceId, url);
          toast('Attachment removed', 'success');
        } catch {
          toast('Failed to remove attachment', 'error');
        }
      },
      null
    );
  };


  const handleDeleteSpace = async () => {
    openConfirmationModal(
      "Delete this space and all of its chats? This can't be undone.",
      async () => {
        try {
          // Now only using Convex through DataContext
          await deleteSpaceFromDataContext(currentSpaceId as string);
          navigate('/spaces');
        } catch (error) {
          console.error('Error deleting space:', error);
          toast('Error deleting space', 'error');
        }
      },
      null
    );
  };

  useEffect(() => {
    const textarea = instructionsTextareaRef.current;
    if (textarea) {
      adjustTextareaHeight(textarea);
    }
  }, [instructions]);

  if (isSpacesLoading) {
    return <LoadingOverlay message="Loading Space..." />;
  }

  if (!currentSpace) {
    return <NotFound linkTo="/spaces" />;
  }

  const adjustTextareaHeight = (element: HTMLTextAreaElement) => {
    element.style.height = 'auto';
    element.style.height = `${Math.min(element.scrollHeight, 400)}px`; // Max height of 400px
  };

  return (
    <div style={{ width: '100%' }}>
      <Header />
      <div className="study-space-page">
        <SpaceUploadModal />
        <div className='space-page-body-container'>
        <div className="space-page-header">
          <div className="space-page-header-left">
            <Book01Icon size={24} />
            {isEditingTitle ? (
              <div className="title-editor">
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === 'Escape') {
                      handleUpdateSpaceTitle();
                    }
                  }}
                  onBlur={() => {
                    if (currentSpace?.title !== title) {
                      handleUpdateSpaceTitle();
                    }
                    setIsEditingTitle(false);
                  }}
                  placeholder="Enter space title..."
                  autoFocus
                />
              </div>
            ) : (
              <h1
                onClick={() => setIsEditingTitle(true)}
                className="editable-title"
              >
                {title}
                <Edit02Icon size={13} className="edit-icon" />
              </h1>
            )}
          </div>
          <div className="space-page-header-right">
            <button
              className="delete-space-btn"
              onClick={() => {
                handleDeleteSpace();
              }}
            >
              Delete Space
            </button>
          </div>
        </div>

        <div className="space-page-instructions-container">
          <div className="space-page-instructions-top">
            <h2>Goals & Objectives</h2>
          </div>
          <div className="space-page-instructions-bottom">
            <textarea
              ref={instructionsTextareaRef}
              value={instructions}
              onChange={(e) => setInstructions(e.target.value)}
              onBlur={() => {
                handleUpdateSpaceInstructions();
              }}
              className="instructions-editor"
              placeholder="What would you like to achieve in this space? Add your goals, objectives, and any important notes..."
            />
          </div>
        </div>

        <div className="study-space-content">
          <div className="study-space-content-header">
            <button
                className="upload-button"
                onClick={() => {
                  const knowledgePercentage = Math.min((currentSpace?.knowledgeCount || 0) / chatConfig.limits.tokens.MAX_SPACE_ATTACHMENTS_TOKENS * 100, 100);
                  if (knowledgePercentage >= 100) {
                    toast('Knowledge capacity is full. Please create a new space.', 'error');
                    return;
                  }
                  openUploadModal();
                }}
              >
                <Upload03Icon size={16} />
                Upload
            </button>

            <button 
              className="create-chat-btn"
              onClick={() => handleCreateSpaceChat(false)}
            >
              <Add01Icon size={16} />
              New Chat
            </button>
          </div>

          <div className="study-space-content-body">
            <div className="section-header">
              <button
                className={`study-space-content-body-header-item ${isAttachmentToggled ? 'active' : ''}`}
                type="button"
                onClick={() => setIsAttachmentToggled(true)}
                tabIndex={0}
              >
                Materials
              </button>
              <button
                className={`study-space-content-body-header-item ${!isAttachmentToggled ? 'active' : ''}`}
                type="button"
                onClick={() => setIsAttachmentToggled(false)}
                tabIndex={0}
              >
                Conversations
              </button>
            </div>

          {isChatsLoading || isSpacesLoading ? (
            <LoadingOverlay />
          ) : (
            <>
            {isAttachmentToggled ? (
              <AttachmentsList
                attachments={spaceAttachments}
                handleResourceClick={handleResourceClick}
                handleAttachmentRemove={handleAttachmentRemove}
              />
            ) : (
              <ChatsSection currentSpaceChats={currentSpaceChats} isSpaceChatsLoading={isSpaceChatsLoading} />
            )}
            </>
          )}
          </div>
        </div>

        </div>
      </div>
    </div>
  );
};

export default StudySpace;